import { LODLevel } from '../Types';

/**
 * Interface for tile metadata from the server
 */
interface TileMetadata {
  level: number;
  rows_per_face: number;
  cols_per_face: number;
}

/**
 * Generate LOD levels configuration from server metadata tiles
 * This ensures LOD levels match the available tile resolutions
 */
export function generateLODLevelsFromMetadata(tiles: TileMetadata[]): LODLevel[] {
  if (!tiles || tiles.length === 0) {
    // Return minimal default if no metadata
    return [
      { level: 1, tilesPerFace: 1, fovThreshold: { min: 0, max: 120 }, priority: 1 }
    ];
  }

  // Sort tiles by level (ascending) to process from highest detail to lowest
  const sortedTiles = [...tiles].sort((a, b) => a.level - b.level);

  const lodLevels: LODLevel[] = [];

  // Map each metadata level to an LOD level
  sortedTiles.forEach((tile, index) => {
    // Use the maximum dimension for tiles per face (in case rows != cols)
    const tilesPerFace = Math.max(tile.rows_per_face, tile.cols_per_face);

    // Skip if this would create a duplicate tiles per face configuration
    if (lodLevels.some(lod => lod.tilesPerFace === tilesPerFace)) {
      return;
    }

    // Calculate LOD level number based on tiles per face
    // LOD 1 = 1x1, LOD 2 = 2x2, LOD 3 = 4x4, LOD 4 = 8x8, etc.
    const lodLevelNumber = Math.log2(tilesPerFace) + 1;

    // Define FOV thresholds based on tile resolution
    // Higher resolution tiles are shown at lower FOV (more zoomed in)
    let fovThreshold: { min: number; max: number };

    if (tilesPerFace === 1) {
      // 1x1 tiles: Base level, widest FOV
      fovThreshold = { min: 60, max: 120 };
    } else if (tilesPerFace === 2) {
      // 2x2 tiles: Medium detail
      fovThreshold = { min: 30, max: 60 };
    } else if (tilesPerFace === 4) {
      // 4x4 tiles: High detail
      fovThreshold = { min: 15, max: 30 };
    } else if (tilesPerFace === 8) {
      // 8x8 tiles: Very high detail
      fovThreshold = { min: 0, max: 15 };
    } else if (tilesPerFace === 16) {
      // 16x16 tiles: Ultra high detail
      fovThreshold = { min: 0, max: 8 };
    } else {
      // For other resolutions, calculate proportionally
      const maxFov = Math.max(10, 120 / tilesPerFace);
      const minFov = Math.max(0, maxFov - 30);
      fovThreshold = { min: minFov, max: maxFov };
    }

    lodLevels.push({
      level: Math.floor(lodLevelNumber),
      tilesPerFace: tilesPerFace,
      fovThreshold: fovThreshold,
      priority: Math.floor(lodLevelNumber) // Higher detail gets higher priority
    });
  });

  // Sort by level number for consistency
  lodLevels.sort((a, b) => a.level - b.level);

  // Adjust FOV thresholds to ensure no gaps
  for (let i = 1; i < lodLevels.length; i++) {
    lodLevels[i].fovThreshold.max = lodLevels[i - 1].fovThreshold.min;
  }

  // Ensure the highest detail level starts at FOV 0
  if (lodLevels.length > 0) {
    lodLevels[lodLevels.length - 1].fovThreshold.min = 0;
  }

  // Ensure the lowest detail level goes up to FOV 120
  if (lodLevels.length > 0) {
    lodLevels[0].fovThreshold.max = 120;
  }

  return lodLevels;
}

/**
 * Validate that LOD levels are compatible with metadata
 */
export function validateLODAgainstMetadata(
  lodLevels: LODLevel[],
  metadataTiles: TileMetadata[]
): { isValid: boolean; issues: string[] } {
  const issues: string[] = [];

  if (!metadataTiles || metadataTiles.length === 0) {
    issues.push('No metadata tiles available');
    return { isValid: false, issues };
  }

  // Get maximum tiles per face from metadata
  const maxMetadataTilesPerFace = Math.max(
    ...metadataTiles.map(t => Math.max(t.rows_per_face, t.cols_per_face))
  );

  // Check each LOD level
  for (const lod of lodLevels) {
    if (lod.tilesPerFace > maxMetadataTilesPerFace) {
      issues.push(
        `LOD level ${lod.level} requests ${lod.tilesPerFace}x${lod.tilesPerFace} tiles ` +
        `but metadata only supports up to ${maxMetadataTilesPerFace}x${maxMetadataTilesPerFace}`
      );
    }
  }

  return {
    isValid: issues.length === 0,
    issues
  };
}

/**
 * Get the best matching metadata level for a given LOD level
 */
export function getMetadataLevelForLOD(
  lodLevel: LODLevel,
  metadataTiles: TileMetadata[]
): TileMetadata | null {
  if (!metadataTiles || metadataTiles.length === 0) {
    return null;
  }

  const targetTilesPerFace = lodLevel.tilesPerFace;

  // First try to find exact match
  const exactMatch = metadataTiles.find(
    m => m.rows_per_face === targetTilesPerFace && m.cols_per_face === targetTilesPerFace
  );

  if (exactMatch) {
    return exactMatch;
  }

  // Find the closest match that can accommodate the requested resolution
  // Prefer metadata with equal or higher resolution
  const compatibleLevels = metadataTiles.filter(
    m => m.rows_per_face >= targetTilesPerFace && m.cols_per_face >= targetTilesPerFace
  );

  if (compatibleLevels.length > 0) {
    // Return the one with the lowest resolution that still fits
    return compatibleLevels.reduce((prev, curr) => {
      const prevMax = Math.max(prev.rows_per_face, prev.cols_per_face);
      const currMax = Math.max(curr.rows_per_face, curr.cols_per_face);
      return currMax < prevMax ? curr : prev;
    });
  }

  // If no compatible level found, return the highest available resolution
  return metadataTiles.reduce((prev, curr) => {
    const prevMax = Math.max(prev.rows_per_face, prev.cols_per_face);
    const currMax = Math.max(curr.rows_per_face, curr.cols_per_face);
    return currMax > prevMax ? curr : prev;
  });
}